/**
 * AppPermissions
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { Card, ListGroup } from 'react-bootstrap';
import Switch from 'components/interaction/Switch/index.jsx';
import TwoStepControl from 'components/interaction/TwoStepControl/index.jsx';
import IconButton from 'components/interaction/IconButton/index.jsx';
import cx from 'utils/cx.js';
import PermissionSelect from './PermissionSelect.jsx';
import messages from './messages.js';
import getSelected from './getSelected.js';
import CustomPermissions from './CustomPermissions.jsx';
import HelpModal from './HelpModal.jsx';
import useIsFoundation from '../../../../useIsFoundation.js';

const Wrapper = styled(Card)`
  .list-group-item {
    display: flex;
    align-items: center;
    & > :first-child {
      flex-grow: 1;
    }
    select.form-control {
      width: auto;
      padding-top: 2px;
      padding-bottom: 2px;
      height: 26px;
      border-radius: 12px;
      background-position-y: 4px;
    }
  }
`;

function AppPermissions(props) {
  const [show, setShow] = useState(false);
  const { onEnable, onChange, settings, template, working } = props;
  const { enabled, defaultPermissions } = settings;
  const roles = [
    { key: 'studentPermissions', label: messages.students },
    { key: 'parentPermissions', label: messages.parents },
    { key: 'employeePermissions', label: messages.employees },
  ];
  if (useIsFoundation()) {
    roles.push({ key: 'staffPermissions', label: messages.staff });
  }
  const handleDefault = (key) => (permissions) => {
    if (!onChange) {
      return;
    }
    onChange('defaultPermissions', { ...defaultPermissions, [key]: permissions });
  };

  return (
    <Wrapper>
      <Card.Header className="d-flex align-items-start border-0">
        {template.switch && <Switch value={enabled} onChange={onEnable} working={working} optimistic className="me-3" aria-label="Enable module" />}
        <div className="flex-grow-1">
          <h4 className="d-inline me-2"><FormattedMessage {...template.header} /></h4>
          {template.headerHelp && <span className="form-text"><FormattedMessage {...template.headerHelp} /></span>}
        </div>
      </Card.Header>
      <Card.Body className={cx(!enabled && 'opacity-50')}>
        <div className="d-flex">
          <h5 className="text-gray flex-grow-1"><FormattedMessage {...messages.defaultPermissions} /></h5>
          {template.options[0].help && <IconButton className="pe-0" variant="link" size="sm" aria-label="Show info" icon="ss-info fs-3" onClick={() => setShow(true)} />}
        </div>
        <ListGroup>
          {roles.map((role) => (
            <ListGroup.Item key={role.key} className="px-0 text-gray">
              <FormattedMessage {...role.label} />
              <TwoStepControl Control={PermissionSelect} options={template.options} onChange={handleDefault(role.key)} value={getSelected(defaultPermissions[role.key], template.options)} disabled={!enabled} />
            </ListGroup.Item>
          ))}
        </ListGroup>
        {template.exceptions && <CustomPermissions {...props} className="mt-5" />}
        {show && <HelpModal onHide={() => setShow(false)} template={template} />}
      </Card.Body>
    </Wrapper>
  );
}

AppPermissions.propTypes = {
  onEnable: PropTypes.func,
  onChange: PropTypes.func,
  working: PropTypes.bool,
  settings: PropTypes.object,
  template: PropTypes.object,
};

export default AppPermissions;
